import React, { useState, useMemo, useCallback } from "react";
import { Modal, Button, Tabs, Typography, Alert, Tag, Space } from "antd";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import CodeEditor from "../common/code-editor";

const { TabPane } = Tabs;

// Utility function to clean data by removing regex and vars from items (but keep duplicate)
const cleanFormData = data => {
  if (!data || !data.items) return data;

  return {
    ...data,
    items: data.items.map(item => {
      const { regex, vars, ...cleanItem } = item;
      return cleanItem; // duplicate field is preserved
    }),
  };
};

// Utility function to clean JSON content
const cleanJsonContent = jsonString => {
  try {
    const data = JSON.parse(jsonString);
    const cleanedData = cleanFormData(data);
    return JSON.stringify(cleanedData, null, 2);
  } catch (error) {
    return jsonString; // Return original if parsing fails
  }
};

// Utility function to clean schema by removing regex and vars from items (but keep duplicate)
const cleanSchema = schema => {
  if (!schema || !schema.properties || !schema.properties.items) return schema;

  const cleanedSchema = JSON.parse(JSON.stringify(schema)); // Deep clone

  if (
    cleanedSchema.properties.items.items &&
    cleanedSchema.properties.items.items.properties
  ) {
    const { regex, vars, ...cleanProperties } =
      cleanedSchema.properties.items.items.properties;
    cleanedSchema.properties.items.items.properties = cleanProperties;

    // Add duplicate field to schema if it doesn't exist
    if (!cleanedSchema.properties.items.items.properties.duplicate) {
      cleanedSchema.properties.items.items.properties.duplicate = {
        type: "boolean",
        title: "Duplicate Status",
        description: "Indicates if this item is a duplicate (readonly)",
      };
    }
  }

  return cleanedSchema;
};

// Utility function to clean uiSchema by removing regex and vars configurations (but add duplicate config)
const cleanUiSchema = uiSchema => {
  if (!uiSchema || !uiSchema.items || !uiSchema.items.items) return uiSchema;

  const cleanedUiSchema = JSON.parse(JSON.stringify(uiSchema)); // Deep clone

  if (cleanedUiSchema.items.items) {
    const { regex, vars, ...cleanItems } = cleanedUiSchema.items.items;
    cleanedUiSchema.items.items = {
      ...cleanItems,
      // Make duplicate field readonly and add warning styling
      duplicate: {
        "ui:readonly": true,
        "ui:widget": "DuplicateWarningWidget",
        "ui:options": {
          label: false, // Hide the default label since we'll show custom warning
        },
      },
    };
  }

  return cleanedUiSchema;
};

// Custom widget for duplicate field that shows warning when true
const DuplicateWarningWidget = props => {
  const { value } = props;

  if (!value) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Alert
        message={
          <Space>
            <Typography.Text strong>Duplicate Detected:</Typography.Text>
            <Typography.Text>This quick word already exists in the system</Typography.Text>
            <Tag color="warning">DUPLICATE</Tag>
          </Space>
        }
        type="warning"
        showIcon
      />
    </div>
  );
};

// Custom widget for script field using CodeEditor
const CodeEditorWidget = props => {
  const { value, onChange, disabled, readonly } = props;

  const handleCodeChange = useCallback(
    instance => {
      const code = instance.getValue();
      onChange(code);
    },
    [onChange],
  );

  return (
    <div style={{ marginTop: 8 }}>
      <CodeEditor
        value={value || ""}
        editable={!disabled && !readonly}
        onChange={handleCodeChange}
        height="300px"
      />
    </div>
  );
};

// Custom widgets object
const customWidgets = {
  CodeEditorWidget: CodeEditorWidget,
  DuplicateWarningWidget: DuplicateWarningWidget,
};

function QuickWordConfigDialog({
  open,
  onClose,
  selectedCommand,
  formData,
  onFormChange,
  onSave,
  isProcessing,
  schema,
  createUiSchema,
  editUiSchema,
  jsonContent,
  onJsonChange,
  jsonError,
}) {
  const [tabValue, setTabValue] = useState(0);

  // Clean the form data to remove regex and vars from items
  const cleanedFormData = useMemo(() => {
    return cleanFormData(formData);
  }, [formData]);

  // Clean the JSON content to remove regex and vars from items
  const cleanedJsonContent = useMemo(() => {
    return cleanJsonContent(jsonContent);
  }, [jsonContent]);

  // Clean the schema to remove regex and vars field definitions
  const cleanedSchema = useMemo(() => {
    return cleanSchema(schema);
  }, [schema]);

  // Clean the uiSchemas to remove regex and vars field configurations
  const cleanedCreateUiSchema = useMemo(() => {
    return cleanUiSchema(createUiSchema);
  }, [createUiSchema]);

  const cleanedEditUiSchema = useMemo(() => {
    return cleanUiSchema(editUiSchema);
  }, [editUiSchema]);

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        selectedCommand && selectedCommand.id
          ? `Edit Command: ${selectedCommand.id}`
          : "Create New Command"
      }
      width="90%"
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={onSave}
          disabled={isProcessing === "save"}
          loading={isProcessing === "save"}
        >
          Save Command
        </Button>,
      ]}
    >
      <Tabs activeKey={String(tabValue)} onChange={handleTabChange} style={{ marginBottom: 16 }}>
        <TabPane tab="Form Editor" key="0">
          {cleanedFormData && (
            <div style={{ paddingTop: 16 }}>
            <Form
              schema={cleanedSchema}
              formData={cleanedFormData}
              onChange={onFormChange}
              validator={validator}
              uiSchema={
                selectedCommand && selectedCommand.id
                  ? cleanedEditUiSchema
                  : cleanedCreateUiSchema
              }
              widgets={customWidgets}
            />
            </div>
          )}
        </TabPane>

        <TabPane tab="JSON Editor" key="1">
          <div style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Edit the command configuration as JSON. Changes will be reflected
              in the form editor.
            </Typography.Text>
            {jsonError && (
              <Alert
                message={jsonError}
                type="error"
                style={{ marginBottom: 16 }}
              />
            )}
            <CodeEditor
              value={cleanedJsonContent}
              editable={true}
              onChange={onJsonChange}
              height="60vh"
            />
          </div>
        </TabPane>
      </Tabs>
    </Modal>
  );
}

export default QuickWordConfigDialog;
